import { useCallback, useSyncExternalStore } from 'react';

/* Preferências de visualização deste navegador: tema, paleta, textura.
   São conveniências pessoais — ficam no localStorage, não no servidor. */

export type Theme = 'light' | 'dark' | null;
export type Palette = 'sinal' | 'grafite' | 'floresta' | 'azul';
export type Texture = 'none' | 'campo' | 'foto';
export interface Prefs { theme: Theme; pal: Palette; tex: Texture }

const KEY = 'ads:prefs';
const root = () => document.documentElement;

function read(): Prefs {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<Prefs>;
    return { theme: p.theme ?? null, pal: p.pal ?? 'sinal', tex: p.tex ?? 'none' };
  } catch { return { theme: null, pal: 'sinal', tex: 'none' }; }
}

let state = read();
const listeners = new Set<() => void>();
/** conta as trocas: os canvas redesenham quando muda */
let version = 0;

function apply(p: Prefs) {
  const r = root();
  if (p.theme) r.dataset.adsTheme = p.theme; else delete r.dataset.adsTheme;
  r.dataset.pal = p.pal;
  r.dataset.tex = p.tex;
}

let swapT: ReturnType<typeof setTimeout> | undefined;
export function setPrefs(patch: Partial<Prefs>) {
  const r = root();
  r.classList.add('swap');
  state = { ...state, ...patch };
  apply(state);
  try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* navegação privada */ }
  clearTimeout(swapT);
  swapT = setTimeout(() => r.classList.remove('swap'), 360);
  requestAnimationFrame(() => { version++; listeners.forEach(l => l()); });
}

const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;
mq?.addEventListener('change', () => { version++; listeners.forEach(l => l()); });

/** O tema que está valendo agora, considerando o sistema. */
export const effectiveTheme = (): 'light' | 'dark' =>
  state.theme ?? (root().getAttribute('data-theme') === 'dark' || mq?.matches ? 'dark' : 'light');

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };

export function usePrefs(): Prefs {
  return useSyncExternalStore(subscribe, () => state);
}
/** Muda sempre que tema/paleta mudam — dependência para redesenhar canvas. */
export function usePaintVersion(): number {
  return useSyncExternalStore(subscribe, () => version);
}
export function useToggleTheme() {
  return useCallback(() => setPrefs({ theme: effectiveTheme() === 'dark' ? 'light' : 'dark' }), []);
}
